import { colors } from '@lmc/common/constants'
import { NAME_MAX_LENGTH } from 'common/constants'
import dayjs from 'dayjs'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { BarChart } from '../../../../components/charts'
import {
  HeaderSection,
  NoDataFound,
  SingleDateSelector,
  Spinner,
  Table
} from '../../../../components/generic'
import Operations from '../../../../operations'
import { GroupsSidebarNavigation } from '../../components/reports'
import {
  getHighChartsSeries,
  getTableData,
  orderByTotal
} from '../../utils/groupReports'

const onDateChange = (newDate, setDate, fetchLists) => {
  setDate(newDate)
  fetchLists(newDate)
}

const renderEmpty = () => {
  return (
    <div style={{ paddingTop: 5 }}>
      <NoDataFound
        heading="Nothing to see here..."
        text="Your group has no To-Do's set up."
      />
    </div>
  )
}

const renderHeader = (date, setDate, fetchLists) => {
  return (
    <div>
      <HeaderSection title="To-Do's Status" />
      <SingleDateSelector
        displayFormat="DD-MM-YYYY"
        onChange={(newDate) => onDateChange(newDate, setDate, fetchLists)}
        currentDate={date}
      />
    </div>
  )
}

export const renderContent = (data, sort, setSort) => {
  const categories = [
    {
      key: 'completed',
      label: 'Completed',
      color: colors.green,
      icon: styles.headerIcons.completed
    },
    {
      key: 'skipped',
      label: 'Skipped',
      color: colors.blue,
      icon: styles.headerIcons.skipped
    },
    {
      key: 'pending',
      label: 'Pending',
      color: colors.orange,
      icon: styles.headerIcons.pending
    },
    {
      key: 'overdue',
      label: 'Overdue',
      color: colors.red,
      icon: styles.headerIcons.overdue
    }
  ]

  const columns = [
    {
      label: 'Site',
      sortable: false,
      width: 300
    },
    ...categories.map((category) => ({
      label: category.label,
      key: category.key,
      type: 'percentage',
      icon: category.icon
    }))
  ]

  const sortedData = orderByTotal(data, sort)
  const series = getHighChartsSeries(sortedData, categories)

  return (
    <div>
      <BarChart
        type="bar"
        height={200 + sortedData.length * 40}
        categories={sortedData.map((d) => (d.keyDoc ? d.keyDoc.name : d.key))}
        series={series.reverse()}
        stacked
        legend={{ reversed: true }}
        yAxisTickFormat={'{value}%'}
        yAxisTickInterval={25}
      />
      <Table
        data={getTableData(sortedData, categories, NAME_MAX_LENGTH)}
        defaultSorted={{ title: 'Overdue', sorted: 'desc' }}
        onSortChange={(newSort) => setSort(newSort)}
        columns={columns}
      />
    </div>
  )
}

export function GroupToDosStatus(props) {
  const { fetchLists, isLoading, data } = props

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchLists()
  }, [])

  const [date, setDate] = useState(dayjs())
  const [sort, setSort] = useState({ key: 'overdue', order: 'desc' })

  return (
    <div style={styles.mainContainer}>
      <StyledNavigation>
        <GroupsSidebarNavigation />
      </StyledNavigation>
      <StyledInnerContainer>
        {renderHeader(date, setDate, props.fetchLists)}
        {isLoading ? (
          <Spinner />
        ) : !data || !data.length ? (
          renderEmpty()
        ) : (
          renderContent(data, sort, setSort)
        )}
      </StyledInnerContainer>
    </div>
  )
}

const styles = {
  dateSelectorContainer: {
    margin: '0 auto',
    paddingTop: 15,
    paddingBottom: 0,
    textAlign: 'left'
  },
  headerIcons: {
    completed: {
      name: 'tick-in-circle',
      color: colors.green
    },
    skipped: {
      name: 'skip',
      color: colors.blue
    },
    pending: {
      name: 'clock',
      color: colors.orange
    },
    overdue: {
      name: 'overdue',
      color: colors.red
    }
  },
  mainContainer: {
    margin: '0 auto',
    maxWidth: 2000,
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    minHeight: 'calc(100vh - 64px)'
  }
}

const StyledInnerContainer = styled.div`
  max-width: 1080px;
  margin-left: auto;
  margin-right: auto;
  margin-top: 24px;
  height: 100%;
  width: 100%
`

const StyledNavigation = styled.div`
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    display: none;
  }
`

GroupToDosStatus.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool
}

const mapStateToProps = (state) => {
  return {
    isLoading: get(state, 'data.requests.group_todos.isLoading'),
    error: get(state, 'data.requests.group_todos.error'),
    data: get(state, 'data.requests.group_todos.response') && [
      ...get(state, 'data.requests.group_todos.response')
    ]
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    fetchLists: (newDate) => {
      // dispatch(Operations.loadList('homes'))
      dispatch(
        Operations.fetchData({
          url: 'aggregations/todos',
          query: newDate ? { date: newDate.toISOString() } : {},
          id: 'group_todos'
        })
      )
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(GroupToDosStatus)
